/**
 * Scheduler Form Page
 * 
 * Create or edit a scheduler (recurring payment).
 * Supports transfer mode when multiple accounts exist.
 * 
 * @validates Requirements 9.1-9.8 - Scheduler management
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSchedulerStore } from '../store/schedulerStore';
import { useAccountStore } from '../store/accountStore';
import { useCategoryStore } from '../store/categoryStore';
import { usePaymentMethodStore } from '../store/paymentMethodStore';
import { Card, Button, Input } from '../components/ui';
import CategorySelector from '../components/CategorySelector';
import { CounterpartySelector } from '../components/common/CounterpartySelector';
import type { Periodicity, OperationDirection, CreateSchedulerRequest, Scheduler, BusinessDayAdjustment } from '@budgira/shared';

// Data passed from operation to pre-fill scheduler
interface OperationToSchedulerData {
  direction: OperationDirection;
  amount: number;
  counterpartyName: string;
  categoryId: string;
  categoryName: string;
  paymentMethodId: string;
  paymentDetails: string;
  reference: string;
  comment: string;
}

interface SchedulerFormData {
  direction: OperationDirection;
  amount: string;
  counterpartyName: string;
  categoryId: string;
  categoryName: string;
  paymentMethodId: string;
  paymentDetails: string;
  reference: string;
  comment: string;
  startDate: string;
  endDate: string;
  periodicity: Periodicity;
  autoPoint: boolean;
  businessDayAdjustment: BusinessDayAdjustment;
  // For edit mode
  counterpartyId?: string;
  nextOccurrence?: string;
}

interface TransferSchedulerFormData {
  amount: string;
  destinationAccountId: string;
  debitCategoryId: string;
  creditCategoryId: string;
  reference: string;
  comment: string;
  startDate: string;
  endDate: string;
  periodicity: Periodicity;
  autoPoint: boolean;
  businessDayAdjustment: BusinessDayAdjustment;
}

const PERIODICITIES: Periodicity[] = [
  'daily',
  'weekly',
  'bi-weekly',
  'monthly',
  'quarterly',
  'semi-annually',
  'annually',
];

const SchedulerFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId: urlAccountId, schedulerId } = useParams<{ accountId?: string; schedulerId?: string }>();
  const { selectedAccount, selectAccount, accounts, fetchAccounts } = useAccountStore();
  const { createScheduler, createTransferScheduler, updateScheduler, getScheduler, isLoading } = useSchedulerStore();
  const { categories, fetchCategories } = useCategoryStore();
  const { paymentMethods, fetchPaymentMethods } = usePaymentMethodStore();

  // Check if we're pre-filling from an operation
  const fromOperation = (location.state as { fromOperation?: OperationToSchedulerData } | null)?.fromOperation;
  
  // Check if we're duplicating from an existing scheduler
  const duplicateFrom = (location.state as { duplicateFrom?: Scheduler } | null)?.duplicateFrom;

  const isEditMode = !!schedulerId;
  const today = new Date().toISOString().split('T')[0] || '';

  // Transfer mode - only available in create mode with 2+ accounts
  const [isTransferMode, setIsTransferMode] = useState(false);
  const canTransfer = !isEditMode && accounts.length >= 2 && !fromOperation && !duplicateFrom;

  const [effectiveAccountId, setEffectiveAccountId] = useState<string | undefined>(urlAccountId);

  const [formData, setFormData] = useState<SchedulerFormData>(() => {
    // If we're duplicating from an existing scheduler
    if (duplicateFrom) {
      return {
        direction: duplicateFrom.direction,
        amount: duplicateFrom.amount.toString(),
        counterpartyName: duplicateFrom.counterparty?.name || '',
        categoryId: duplicateFrom.categories?.[0]?.categoryId || '',
        categoryName: duplicateFrom.categories?.[0]?.category?.name || '',
        paymentMethodId: duplicateFrom.paymentMethodId || '',
        paymentDetails: duplicateFrom.paymentDetails || '',
        reference: duplicateFrom.reference || '',
        comment: duplicateFrom.comment || '',
        startDate: today,
        endDate: '',
        periodicity: duplicateFrom.periodicity,
        autoPoint: duplicateFrom.autoPoint,
        businessDayAdjustment: duplicateFrom.businessDayAdjustment || 'none',
      };
    }
    // If we have data from an operation, pre-fill the form
    if (fromOperation) {
      return {
        direction: fromOperation.direction,
        amount: fromOperation.amount.toString(),
        counterpartyName: fromOperation.counterpartyName,
        categoryId: fromOperation.categoryId,
        categoryName: fromOperation.categoryName,
        paymentMethodId: fromOperation.paymentMethodId,
        paymentDetails: fromOperation.paymentDetails || '',
        reference: fromOperation.reference,
        comment: fromOperation.comment,
        startDate: today,
        endDate: '',
        periodicity: 'monthly',
        autoPoint: false,
        businessDayAdjustment: 'none',
      };
    }
    // Default empty form
    return {
      direction: 'debit',
      amount: '',
      counterpartyName: '',
      categoryId: '',
      categoryName: '',
      paymentMethodId: '',
      paymentDetails: '',
      reference: '',
      comment: '',
      startDate: today,
      endDate: '',
      periodicity: 'monthly',
      autoPoint: false,
      businessDayAdjustment: 'none',
    };
  });

  const [transferData, setTransferData] = useState<TransferSchedulerFormData>({
    amount: '',
    destinationAccountId: '',
    debitCategoryId: '',
    creditCategoryId: '',
    reference: '',
    comment: '',
    startDate: today,
    endDate: '',
    periodicity: 'monthly',
    autoPoint: false,
    businessDayAdjustment: 'none',
  });

  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingScheduler, setIsLoadingScheduler] = useState(isEditMode);

  useEffect(() => {
    if (accounts.length === 0) {
      fetchAccounts();
    }
  }, [accounts.length, fetchAccounts]);

  useEffect(() => {
    if (categories.length === 0) {
      fetchCategories();
    }
  }, [categories.length, fetchCategories]);

  useEffect(() => {
    if (paymentMethods.length === 0) {
      fetchPaymentMethods();
    }
  }, [paymentMethods.length, fetchPaymentMethods]);

  useEffect(() => {
    if (urlAccountId && !isEditMode) {
      selectAccount(urlAccountId);
      setEffectiveAccountId(urlAccountId);
    }
  }, [urlAccountId, selectAccount, isEditMode]);

  useEffect(() => {
    if (isEditMode && schedulerId) {
      loadScheduler(schedulerId);
    }
  }, [isEditMode, schedulerId]);

  // Set default payment method when loaded (only if not pre-filled from operation)
  useEffect(() => {
    if (paymentMethods.length > 0 && !formData.paymentMethodId && !isEditMode && !fromOperation) {
      setFormData(prev => ({ ...prev, paymentMethodId: paymentMethods[0]?.id || '' }));
    }
  }, [paymentMethods, formData.paymentMethodId, isEditMode, fromOperation]);

  // Set default destination account when transfer mode is enabled
  useEffect(() => {
    if (isTransferMode && !transferData.destinationAccountId && accounts.length >= 2 && effectiveAccountId) {
      const otherAccounts = accounts.filter(a => a.id !== effectiveAccountId);
      const firstOtherAccount = otherAccounts[0];
      if (firstOtherAccount) {
        setTransferData(prev => ({ ...prev, destinationAccountId: firstOtherAccount.id }));
      }
    }
  }, [isTransferMode, accounts, effectiveAccountId, transferData.destinationAccountId]);

  const loadScheduler = async (id: string) => {
    setIsLoadingScheduler(true);
    try {
      const scheduler = await getScheduler(id);
      if (scheduler) {
        setEffectiveAccountId(scheduler.accountId);
        selectAccount(scheduler.accountId);

        setFormData({
          direction: scheduler.direction,
          amount: scheduler.amount.toString(),
          counterpartyName: scheduler.counterparty?.name || '',
          categoryId: scheduler.categories?.[0]?.categoryId || '',
          categoryName: scheduler.categories?.[0]?.category?.name || '',
          paymentMethodId: scheduler.paymentMethodId || '',
          paymentDetails: scheduler.paymentDetails || '',
          reference: scheduler.reference || '',
          comment: scheduler.comment || '',
          startDate: scheduler.startDate,
          endDate: scheduler.endDate || '',
          periodicity: scheduler.periodicity,
          autoPoint: scheduler.autoPoint,
          businessDayAdjustment: scheduler.businessDayAdjustment || 'none',
          counterpartyId: scheduler.counterpartyId,
          nextOccurrence: scheduler.nextOccurrence,
        });
      }
    } finally {
      setIsLoadingScheduler(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!effectiveAccountId) {
      setFormError(t('operations.selectAccount'));
      return;
    }

    // Transfer mode submission
    if (isTransferMode) {
      if (!transferData.amount || parseFloat(transferData.amount) <= 0) {
        setFormError(t('operations.form.errors.amountRequired'));
        return;
      }
      if (!transferData.startDate) {
        setFormError(t('schedulers.form.errors.startDateRequired'));
        return;
      }
      if (!transferData.destinationAccountId) {
        setFormError(t('transfer.errors.destinationRequired'));
        return;
      }
      if (!transferData.debitCategoryId) {
        setFormError(t('transfer.errors.debitCategoryRequired'));
        return;
      }
      if (!transferData.creditCategoryId) {
        setFormError(t('transfer.errors.creditCategoryRequired'));
        return;
      }

      setIsSaving(true);
      try {
        const result = await createTransferScheduler({
          sourceAccountId: effectiveAccountId,
          destinationAccountId: transferData.destinationAccountId,
          startDate: transferData.startDate,
          endDate: transferData.endDate || undefined,
          amount: parseFloat(transferData.amount),
          periodicity: transferData.periodicity,
          debitCategoryId: transferData.debitCategoryId,
          creditCategoryId: transferData.creditCategoryId,
          reference: transferData.reference.trim() || undefined,
          comment: transferData.comment.trim() || undefined,
          autoPoint: transferData.autoPoint,
          businessDayAdjustment: transferData.businessDayAdjustment,
        });

        if (result) {
          navigate(`/accounts/${effectiveAccountId}/schedulers`);
        }
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Normal scheduler submission
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setFormError(t('operations.form.errors.amountRequired'));
      return;
    }
    if (!formData.counterpartyName.trim()) {
      setFormError(t('operations.form.errors.counterpartyRequired'));
      return;
    }
    if (!formData.startDate) {
      setFormError(t('schedulers.form.errors.startDateRequired'));
      return;
    }
    if (!formData.categoryId) {
      setFormError(t('schedulers.form.errors.categoryRequired'));
      return;
    }
    if (!formData.paymentMethodId) {
      setFormError(t('schedulers.form.errors.paymentMethodRequired'));
      return;
    }

    setIsSaving(true);
    try {
      const amount = parseFloat(formData.amount);

      if (isEditMode && schedulerId) {
        await updateScheduler(schedulerId, {
          direction: formData.direction,
          amount,
          counterpartyId: formData.counterpartyId,
          paymentMethodId: formData.paymentMethodId,
          categories: [{ categoryId: formData.categoryId, amount }],
          paymentDetails: formData.paymentDetails.trim() || null, // Send null to clear
          reference: formData.reference.trim() || null, // Send null to clear
          comment: formData.comment.trim() || null, // Send null to clear
          endDate: formData.endDate || null,
          nextOccurrence: formData.nextOccurrence,
          periodicity: formData.periodicity,
          autoPoint: formData.autoPoint,
          businessDayAdjustment: formData.businessDayAdjustment,
        });
      } else {
        const createData: CreateSchedulerRequest = {
          direction: formData.direction,
          amount,
          counterpartyId: '',
          paymentMethodId: formData.paymentMethodId,
          categories: [{ categoryId: formData.categoryId, amount }],
          paymentDetails: formData.paymentDetails.trim() || undefined,
          reference: formData.reference.trim() || undefined,
          comment: formData.comment.trim() || undefined,
          startDate: formData.startDate,
          endDate: formData.endDate || undefined,
          periodicity: formData.periodicity,
          autoPoint: formData.autoPoint,
          businessDayAdjustment: formData.businessDayAdjustment,
        };

        await createScheduler(effectiveAccountId, {
          ...createData,
          counterpartyName: formData.counterpartyName.trim(),
        } as CreateSchedulerRequest & { counterpartyName: string });
      }

      navigate(`/accounts/${effectiveAccountId}/schedulers`);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof SchedulerFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleTransferChange = (field: keyof TransferSchedulerFormData, value: string | boolean) => {
    setTransferData(prev => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleTransferModeToggle = (enabled: boolean) => {
    setIsTransferMode(enabled);
    setFormError(null);
    // Sync common fields
    if (enabled) {
      setTransferData(prev => ({
        ...prev,
        amount: formData.amount,
        startDate: formData.startDate,
        endDate: formData.endDate,
        periodicity: formData.periodicity,
        reference: formData.reference,
        comment: formData.comment,
        autoPoint: formData.autoPoint,
        businessDayAdjustment: formData.businessDayAdjustment,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        amount: transferData.amount,
        startDate: transferData.startDate,
        endDate: transferData.endDate,
        periodicity: transferData.periodicity,
        reference: transferData.reference,
        comment: transferData.comment,
        autoPoint: transferData.autoPoint,
        businessDayAdjustment: transferData.businessDayAdjustment,
      }));
    }
  };

  if (isLoadingScheduler) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-500 dark:text-slate-400">{t('common.loading')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!selectedAccount && !isLoading && !isLoadingScheduler) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-500 dark:text-slate-400">{t('operations.selectAccount')}</p>
          </div>
        </Card>
      </div>
    );
  }

  // Get destination accounts (all accounts except current)
  const destinationAccounts = accounts.filter(a => a.id !== effectiveAccountId);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {isTransferMode 
            ? t('transfer.schedulerTitle')
            : isEditMode 
              ? t('schedulers.form.editTitle') 
              : t('schedulers.form.createTitle')}
        </h1>
        <p className="text-slate-500 dark:text-slate-400">
          {selectedAccount?.name}
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error message */}
          {formError && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-600 dark:text-red-400">
              {formError}
            </div>
          )}

          {/* Transfer Mode Toggle - only show in create mode with 2+ accounts */}
          {canTransfer && (
            <div className="flex items-center gap-3 p-4 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isTransferMode}
                  onChange={(e) => handleTransferModeToggle(e.target.checked)}
                  className="w-4 h-4 text-violet-600 rounded"
                />
                <span className="font-medium text-violet-900 dark:text-violet-100">
                  {t('transfer.toggle')}
                </span>
              </label>
              <span className="text-sm text-violet-600 dark:text-violet-400">
                {t('transfer.schedulerDescription')}
              </span>
            </div>
          )}

          {isTransferMode ? (
            /* Transfer Mode Form */
            <>
              {/* Destination Account */}
              <div>
                <label htmlFor="destinationAccountId" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('transfer.destinationAccount')} *
                </label>
                <select
                  id="destinationAccountId"
                  value={transferData.destinationAccountId}
                  onChange={(e) => handleTransferChange('destinationAccountId', e.target.value)}
                  disabled={isSaving}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                >
                  <option value="">{t('transfer.selectDestination')}</option>
                  {destinationAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div>
                <label htmlFor="transferAmount" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.amount')} *
                </label>
                <Input
                  id="transferAmount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={transferData.amount}
                  onChange={(e) => handleTransferChange('amount', e.target.value)}
                  placeholder="0.00"
                  disabled={isSaving}
                />
              </div>

              {/* Periodicity */}
              <div>
                <label htmlFor="transferPeriodicity" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('schedulers.form.periodicity')} *
                </label>
                <select
                  id="transferPeriodicity"
                  value={transferData.periodicity}
                  onChange={(e) => handleTransferChange('periodicity', e.target.value)}
                  disabled={isSaving}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                >
                  {PERIODICITIES.map(p => (
                    <option key={p} value={p}>
                      {t(`schedulers.periodicity.${p === 'bi-weekly' ? 'biWeekly' : p === 'semi-annually' ? 'semiAnnually' : p}`)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label htmlFor="transferStartDate" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('schedulers.form.startDate')} *
                </label>
                <Input
                  id="transferStartDate"
                  type="date"
                  value={transferData.startDate}
                  onChange={(e) => handleTransferChange('startDate', e.target.value)}
                  disabled={isSaving}
                />
              </div>

              {/* End Date */}
              <div>
                <label htmlFor="transferEndDate" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('schedulers.form.endDate')}
                </label>
                <Input
                  id="transferEndDate"
                  type="date"
                  value={transferData.endDate}
                  onChange={(e) => handleTransferChange('endDate', e.target.value)}
                  disabled={isSaving}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('schedulers.form.endDateHelp')}</p>
              </div>

              {/* Debit Category (for source account) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('transfer.debitCategory')} *
                </label>
                <CategorySelector
                  value={transferData.debitCategoryId}
                  onChange={(categoryId) => {
                    setTransferData(prev => ({ ...prev, debitCategoryId: categoryId || '' }));
                    setFormError(null);
                  }}
                  filterType="debit"
                  placeholder={t('categories.selector.placeholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Credit Category (for destination account) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('transfer.creditCategory')} *
                </label>
                <CategorySelector
                  value={transferData.creditCategoryId}
                  onChange={(categoryId) => {
                    setTransferData(prev => ({ ...prev, creditCategoryId: categoryId || '' }));
                    setFormError(null);
                  }}
                  filterType="credit"
                  placeholder={t('categories.selector.placeholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Payment Method - forced to Virement, read-only */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.paymentMethod')}
                </label>
                <div className="w-full px-4 py-2.5 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-400 cursor-not-allowed">
                  {t('transfer.paymentMethodVirement')}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t('transfer.paymentMethodFixed')}
                </p>
              </div>

              {/* Reference */}
              <div>
                <label htmlFor="transferReference" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.reference')}
                </label>
                <Input
                  id="transferReference"
                  type="text"
                  value={transferData.reference}
                  onChange={(e) => handleTransferChange('reference', e.target.value)}
                  placeholder={t('operations.form.referencePlaceholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Comment */}
              <div>
                <label htmlFor="transferComment" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.comment')}
                </label>
                <textarea
                  id="transferComment"
                  value={transferData.comment}
                  onChange={(e) => handleTransferChange('comment', e.target.value)}
                  placeholder={t('operations.form.commentPlaceholder')}
                  disabled={isSaving}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors resize-none"
                />
              </div>

              {/* Business Day Adjustment */}
              <div>
                <label htmlFor="transferBusinessDayAdjustment" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('schedulers.form.businessDayAdjustment')}
                </label>
                <select
                  id="transferBusinessDayAdjustment"
                  value={transferData.businessDayAdjustment}
                  onChange={(e) => handleTransferChange('businessDayAdjustment', e.target.value)}
                  disabled={isSaving}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                >
                  <option value="none">{t('schedulers.businessDayAdjustment.none')}</option>
                  <option value="previous">{t('schedulers.businessDayAdjustment.previous')}</option>
                  <option value="next">{t('schedulers.businessDayAdjustment.next')}</option>
                </select>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('schedulers.form.businessDayAdjustmentHelp')}</p>
              </div>

              {/* Auto Point */}
              <div className="flex items-center gap-2">
                <input
                  id="transferAutoPoint"
                  type="checkbox"
                  checked={transferData.autoPoint}
                  onChange={(e) => handleTransferChange('autoPoint', e.target.checked)}
                  disabled={isSaving}
                  className="w-4 h-4 text-violet-600 rounded"
                />
                <label htmlFor="transferAutoPoint" className="text-sm text-slate-700 dark:text-slate-300">
                  {t('schedulers.form.autoPoint')}
                </label>
              </div>
            </>
          ) : (
            /* Normal Scheduler Form */
            <>
              {/* Direction */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('operations.form.direction')} *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="direction"
                      value="debit"
                      checked={formData.direction === 'debit'}
                      onChange={() => handleChange('direction', 'debit')}
                      className="w-4 h-4 text-violet-600"
                    />
                    <span className="text-red-600 dark:text-red-400 font-medium">{t('operations.form.debit')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="direction"
                      value="credit"
                      checked={formData.direction === 'credit'}
                      onChange={() => handleChange('direction', 'credit')}
                      className="w-4 h-4 text-violet-600"
                    />
                    <span className="text-green-600 dark:text-green-400 font-medium">{t('operations.form.credit')}</span>
                  </label>
                </div>
              </div>

              {/* Amount */}
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.amount')} *
                </label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="0.00"
                  disabled={isSaving}
                />
              </div>

              {/* Counterparty */}
              <div>
                <label htmlFor="counterpartyName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.counterparty')} *
                </label>
                <CounterpartySelector
                  value={formData.counterpartyName}
                  onChange={(name, counterpartyId) => {
                    setFormData(prev => ({
                      ...prev,
                      counterpartyName: name,
                      counterpartyId: counterpartyId,
                    }));
                    setFormError(null);
                  }}
                  placeholder={t('operations.form.counterpartyPlaceholder')}
                  disabled={isSaving || isEditMode}
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.category')} *
                </label>
                <CategorySelector
                  value={formData.categoryId}
                  onChange={(categoryId, categoryName) => {
                    setFormData(prev => ({
                      ...prev,
                      categoryId: categoryId || '',
                      categoryName: categoryName || ''
                    }));
                    setFormError(null);
                  }}
                  filterType={formData.direction === 'debit' ? 'debit' : 'credit'}
                  placeholder={t('categories.selector.placeholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Payment Method */}
              <div>
                <label htmlFor="paymentMethodId" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.paymentMethod')} *
                </label>
                <select
                  id="paymentMethodId"
                  value={formData.paymentMethodId}
                  onChange={(e) => handleChange('paymentMethodId', e.target.value)}
                  disabled={isSaving}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                >
                  <option value="">{t('operations.form.paymentMethod')}</option>
                  {paymentMethods.map(pm => (
                    <option key={pm.id} value={pm.id}>
                      {pm.logoUrl} {pm.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Details (check number, card number, etc.) */}
              <div>
                <label htmlFor="paymentDetails" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.paymentDetails')}
                </label>
                <Input
                  id="paymentDetails"
                  type="text"
                  value={formData.paymentDetails}
                  onChange={(e) => handleChange('paymentDetails', e.target.value)}
                  placeholder={t('operations.form.paymentDetailsPlaceholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Periodicity */}
              <div>
                <label htmlFor="periodicity" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('schedulers.form.periodicity')} *
                </label>
                <select
                  id="periodicity"
                  value={formData.periodicity}
                  onChange={(e) => handleChange('periodicity', e.target.value)}
                  disabled={isSaving}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                >
                  {PERIODICITIES.map(p => (
                    <option key={p} value={p}>
                      {t(`schedulers.periodicity.${p === 'bi-weekly' ? 'biWeekly' : p === 'semi-annually' ? 'semiAnnually' : p}`)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('schedulers.form.startDate')} *
                </label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  disabled={isSaving || isEditMode}
                />
              </div>

              {/* Next Occurrence - only in edit mode */}
              {isEditMode && (
                <div>
                  <label htmlFor="nextOccurrence" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('schedulers.form.nextOccurrence')}
                  </label>
                  <Input
                    id="nextOccurrence"
                    type="date"
                    value={formData.nextOccurrence || ''}
                    onChange={(e) => handleChange('nextOccurrence', e.target.value)}
                    disabled={isSaving}
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('schedulers.form.nextOccurrenceHelp')}</p>
                </div>
              )}

              {/* End Date */}
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('schedulers.form.endDate')}
                </label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  disabled={isSaving}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('schedulers.form.endDateHelp')}</p>
              </div>

              {/* Reference */}
              <div>
                <label htmlFor="reference" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.reference')}
                </label>
                <Input
                  id="reference"
                  type="text"
                  value={formData.reference}
                  onChange={(e) => handleChange('reference', e.target.value)}
                  placeholder={t('operations.form.referencePlaceholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Comment */}
              <div>
                <label htmlFor="comment" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('operations.form.comment')}
                </label>
                <textarea
                  id="comment"
                  value={formData.comment}
                  onChange={(e) => handleChange('comment', e.target.value)}
                  placeholder={t('operations.form.commentPlaceholder')}
                  disabled={isSaving}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors resize-none"
                />
              </div>

              {/* Business Day Adjustment */}
              <div>
                <label htmlFor="businessDayAdjustment" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('schedulers.form.businessDayAdjustment')}
                </label>
                <select
                  id="businessDayAdjustment"
                  value={formData.businessDayAdjustment}
                  onChange={(e) => handleChange('businessDayAdjustment', e.target.value)}
                  disabled={isSaving}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                >
                  <option value="none">{t('schedulers.businessDayAdjustment.none')}</option>
                  <option value="previous">{t('schedulers.businessDayAdjustment.previous')}</option>
                  <option value="next">{t('schedulers.businessDayAdjustment.next')}</option>
                </select>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('schedulers.form.businessDayAdjustmentHelp')}</p>
              </div>

              {/* Auto Point */}
              <div className="flex items-center gap-2">
                <input
                  id="autoPoint"
                  type="checkbox"
                  checked={formData.autoPoint}
                  onChange={(e) => handleChange('autoPoint', e.target.checked)}
                  disabled={isSaving}
                  className="w-4 h-4 text-violet-600 rounded"
                />
                <label htmlFor="autoPoint" className="text-sm text-slate-700 dark:text-slate-300">
                  {t('schedulers.form.autoPoint')}
                </label>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(effectiveAccountId ? `/accounts/${effectiveAccountId}/schedulers` : '/schedulers')}
              disabled={isSaving}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="flex-1"
            >
              {isSaving ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('common.saving')}
                </span>
              ) : (
                isTransferMode
                  ? t('transfer.createScheduler')
                  : isEditMode 
                    ? t('common.save') 
                    : t('schedulers.form.create')
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default SchedulerFormPage;
